/**
 * LoadingBlank — Full-page branded loading screen: the static Sendly logo with
 * an animated "S" progress sweep and a localized loading label underneath.
 *
 * It is intentionally pixel-identical to the pre-React boot screen in
 * index.html, so the hand-off boot-screen → React loader → content reads
 * as ONE continuous loader instead of a sequence of different spinners/blanks.
 * Shown immediately (no fade-in delay) so there's no blank gap when it replaces
 * the boot screen. Used everywhere a full-page loading state is needed (auth
 * resolution, gym-shell gates, the root Suspense fallback, GymSelector).
 */
import React from "react";
import { getTheme } from "../utils";
import { localStore } from "../utils/safeStorage";
import { useAppTheme } from "../contexts/ThemeContext";

/**
 * @param {"light"|"dark"} theme
 * @param {string} [label] Optional override for the loading text (defaults to a
 *                         localized "Betöltés…" / "Loading…").
 */
export function LoadingBlank({ label, stage }) {
  const { resolvedTheme: theme } = useAppTheme();
  // Boot-stall watchdog breadcrumb (see installBootWatchdog in crashLogger.js):
  // a white screen with no thrown error is a render gate stuck on this loader.
  // Track that a full-page loader is mounted (__sendlyLoaders) and which gate it
  // represents (__sendlyBootStage) so the watchdog can capture WHERE it hung.
  // Purely observational — nothing here affects rendering.
  React.useEffect(() => {
    try {
      window.__sendlyLoaders = (window.__sendlyLoaders || 0) + 1;
      if (stage) window.__sendlyBootStage = stage;
    } catch (e) { /* SSR / no window */ }
    return () => {
      try {
        window.__sendlyLoaders = Math.max(0, (window.__sendlyLoaders || 1) - 1);
        // ── The "we booted" signal, and it MUST live here ──
        // The watchdog only reports while `__sendlyBooted` is false, so whatever
        // sets it defines which stalls are reportable. It used to be set by
        // `MainApp` when auth resolved — but `MainApp` mounts on the gym routes
        // only, so on /profile, /, /calendar, /admin, /version, /legal/* and the
        // embeds it was NEVER set, and the watchdog degenerated into "is any
        // loader on screen at t=20s?". A perfectly healthy /profile open (its
        // chunk still downloading at the 20s mark) reported itself as a
        // white-screen hang — twice, in production.
        //
        // Dropping to zero loaders is the route-agnostic version of the same
        // fact: some route got past ALL its gates and put real content on
        // screen. Crucially it is NOT "auth resolved" — a hung
        // slug resolution (GymProvider's resolveSlug) leaves auth perfectly happy while the app sits on
        // the gym-resolve loader forever, and that stall must stay reportable.
        //
        // Dev-only wrinkle: StrictMode's double-invoked effects run this cleanup
        // once on the very first loader mount, so the watchdog effectively
        // disarms in `npm start`. It is a production diagnostic; not worth
        // complicating this for.
        if (window.__sendlyLoaders === 0) window.__sendlyBooted = true;
      } catch (e) { /* no window */ }
    };
  }, [stage]);
  const th = getTheme(theme);
  let text = label;
  if (!text) {
    const en = (localStore.get("sendly-lang") || "hu").slice(0, 2) === "en";
    text = en ?"Loading…" : "Betöltés…";
  }
  const nameColor = theme === "dark" ? "#9fc3d4" : "#376f8b";
  return (
    <div style={{
      minHeight: "100vh", background: th.bg,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16,
    }}>
      <style>{`@keyframes sendly-s-draw{0%{stroke-dashoffset:100}50%{stroke-dashoffset:0}100%{stroke-dashoffset:-100}}@media(prefers-reduced-motion:reduce){.sendly-loader-s{animation:none!important;stroke-dashoffset:0!important}}`}</style>
      <svg width="96" height="96" viewBox="0 0 128 128" fill="none" style={{ filter: "drop-shadow(0 8px 24px rgba(55,111,139,0.22))" }} aria-hidden="true">
        <rect width="128" height="128" rx="28" fill="#376f8b" />
        {/* Faint base S — keeps the full logo readable at all times */}
        <path d="M88 18 C88 18 42 18 42 44 C42 62 88 62 88 80 C88 106 42 106 42 106" stroke="rgba(255,255,255,0.25)" strokeWidth="7" strokeLinecap="round" fill="none" />
        {/* Bright S that draws then clears = the progress sweep */}
        <path className="sendly-loader-s" d="M88 18 C88 18 42 18 42 44 C42 62 88 62 88 80 C88 106 42 106 42 106" stroke="#fff" strokeWidth="7" strokeLinecap="round" fill="none" pathLength="100" strokeDasharray="100" style={{ animation: "sendly-s-draw 1.8s ease-in-out infinite" }} />
        <circle cx="76" cy="22" r="8" fill="#eab308" />
        <circle cx="50" cy="36" r="8" fill="#dc2626" />
        <circle cx="74" cy="58" r="8" fill="rgba(255,255,255,0.85)" />
        <circle cx="84" cy="78" r="8" fill="#3d8b37" />
        <circle cx="54" cy="98" r="8" fill="#eab308" />
      </svg>
      <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: 14, color: nameColor, opacity: 0.9 }}>{text}</div>
    </div>
  );
}
